import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    // [FROST BUDDY] On Edgemesh (frostbuddy.com) the cart bubble is wrapped in <dynamic>
    // (snippets/cart-bubble.liquid). Edgemesh delivers that wrapper empty and injects the
    // bubble markup asynchronously, so at connect time the required [ref] children don't exist
    // yet: super.connectedCallback() -> #updateRefs() throws MissingRefError. That throw would
    // abort the rest of this method, so the cartLinesUpdate listener would never attach and the
    // nav bubble would stop updating live (only a full reload, which re-injects a fresh count,
    // would correct it — that was the reported bug, confirmed via Playwright on the live site).
    // Fix: attach the listeners FIRST, then swallow the ref-resolution throw. Refs are re-resolved
    // lazily from the live DOM on each update (see refreshBubbleRefs). No-op on non-Edgemesh hosts,
    // where the refs resolve normally at connect and super.connectedCallback() does not throw.
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);

    try {
      super.connectedCallback();
    } catch (error) {
      // Only tolerate the expected missing-ref case (Edgemesh timing); re-throw anything else.
      if (/** @type {Error} */ (error)?.name !== 'MissingRefError') throw error;
    }

    this.ensureCartBubbleIsCorrect();
  }

  /**
   * [FROST BUDDY] Re-resolve the bubble refs from the live DOM. Needed because Edgemesh's async
   * replacement of the <dynamic> wrapper leaves this.refs empty (the base MissingRefError above
   * skips the MutationObserver that would otherwise re-sync them). Called before every read/write
   * so we always target the on-screen nodes. Returns true once all three refs resolve.
   * @returns {boolean}
   */
  refreshBubbleRefs = () => {
    const cartBubble = this.querySelector('[ref="cartBubble"]');
    const cartBubbleText = this.querySelector('[ref="cartBubbleText"]');
    const cartBubbleCount = this.querySelector('[ref="cartBubbleCount"]');

    if (cartBubble) this.refs.cartBubble = /** @type {HTMLElement} */ (cartBubble);
    if (cartBubbleText) this.refs.cartBubbleText = /** @type {HTMLElement} */ (cartBubbleText);
    if (cartBubbleCount) this.refs.cartBubbleCount = /** @type {HTMLElement} */ (cartBubbleCount);

    return Boolean(cartBubble && cartBubbleText && cartBubbleCount);
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartLinesUpdateEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        const itemCount = cart?.totalQuantity ?? detail?.itemCount ?? 0;

        // [FROST BUDDY] Re-point refs to the live (Edgemesh-injected) nodes before rendering.
        if (!this.refreshBubbleRefs()) return;
        this.renderCartBubble(itemCount);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-icon] Event promise rejected:', error);
      });
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The absolute number of items in the cart.
   * @param {boolean} [animate=true] - Whether to animate the bubble.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // [FROST BUDDY] Re-resolve refs from the live DOM first (Edgemesh leaves this.refs empty).
    this.refreshBubbleRefs();

    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
